// frontend/src/components/playground/StrategyCard.jsx
/**
 * Strategy Card Component - Glass-morphic card design
 * Front shows title and AI-generated expert insight
 * Back shows projected impact
 */

import React, { useState } from 'react';
import { motion, useDragControls } from 'framer-motion';
import {
  InformationCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const StrategyCard = ({ card, onDragStart, onDragEnd, onClick, disabled = false }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const dragControls = useDragControls();

  const getCardColor = (rarity) => {
    switch (rarity) {
      case 'legendary':
        return 'from-aurora-gold to-yellow-400';
      case 'epic':
        return 'from-aurora-purple to-pink-500';
      case 'rare':
        return 'from-aurora-blue to-cyan-400';
      case 'uncommon':
        return 'from-green-500 to-emerald-400';
      default:
        return 'from-gray-600 to-gray-400';
    }
  };

  const getCardIcon = (cardType) => {
    switch (cardType) {
      case 'dividend_pivot':
        return '💰';
      case 'growth_accelerator':
        return '🚀';
      case 'conservative_rebalance':
        return '🛡️';
      case 'aggressive_rebalance':
        return '⚡';
      case 'bear_market':
        return '📉';
      case 'bull_market':
        return '📈';
      default:
        return '🎴';
    }
  };

  const handleDragEnd = (event, info) => {
    if (!disabled && !isFlipped && onDragEnd) {
      // Get the final position from the info object
      // info.point gives screen coordinates
      const x = info.point.x;
      const y = info.point.y;
      
      // Create a synthetic event with position
      const syntheticEvent = {
        clientX: x,
        clientY: y,
        point: info.point,
        offset: info.offset,
        velocity: info.velocity
      };
      
      onDragEnd(syntheticEvent);
    }
  };

  return (
    <motion.div
      className="relative perspective-1000 w-full"
      style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
      drag={!disabled && !isFlipped ? true : false}
      dragControls={dragControls}
      dragConstraints={false} // Allow free dragging
      dragElastic={0.2}
      dragMomentum={false}
      onDragStart={() => {
        if (!disabled && !isFlipped && onDragStart) {
          onDragStart();
        }
      }}
      onDragEnd={(event, info) => handleDragEnd(event, info)}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      animate={{ rotateY: isFlipped ? 180 : 0 }}
      transition={{ duration: 0.6, type: "spring" }}
    >
      {/* Card Container */}
      <div className="relative w-full h-64" style={{ transformStyle: 'preserve-3d', isolation: 'isolate' }}>
        {/* Front of Card */}
        <motion.div
          className="absolute inset-0 w-full h-full bg-glass-card backdrop-blur-xl rounded-xl border border-aurora-purple/30 p-4 cursor-grab active:cursor-grabbing"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(0deg)' }}
          onClick={(e) => {
            if (!disabled && !isFlipped && onClick) {
              e.stopPropagation();
              onClick();
            }
          }}
        >
          {/* Card Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getCardIcon(card.card_type)}</span>
              <div>
                <div className="text-sm font-semibold text-white">{card.title}</div>
                <div className="text-xs text-gray-400 capitalize">{card.rarity}</div>
              </div>
            </div>
            
            {card.played && (
              <CheckCircleIcon className="w-5 h-5 text-aurora-green" />
            )}
          </div>

          {/* Card Description */}
          <p className="text-sm text-gray-300 mb-4 line-clamp-3">
            {card.description}
          </p>

          {/* Expert Insight */}
          <div className="bg-aurora-purple/10 rounded-lg p-3 mb-3 border border-aurora-purple/20">
            <div className="flex items-start gap-2">
              <InformationCircleIcon className="w-4 h-4 text-aurora-purple mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-200 italic">
                "{card.expert_insight || 'This move could impact your portfolio strategy.'}"
              </p>
            </div>
          </div>

          {/* Card Footer */}
          <div className="flex items-center justify-between">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(!isFlipped);
              }}
              className="text-xs text-aurora-purple hover:text-aurora-blue transition flex items-center gap-1"
            >
              <ArrowPathIcon className="w-3 h-3" />
              Flip
            </button>
            
            {!disabled && !isFlipped && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) onClick();
                }}
                className="text-xs bg-aurora-purple/20 hover:bg-aurora-purple/30 text-aurora-purple px-3 py-1 rounded-lg transition font-medium"
              >
                Play Card
              </button>
            )}
            
            {disabled && (
              <span className="text-xs text-gray-500">Already played</span>
            )}
          </div>

          {/* Rarity Gradient Border */}
          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${getCardColor(card.rarity)} opacity-20 -z-10`} />
        </motion.div>

        {/* Back of Card */}
        <motion.div
          className="absolute inset-0 w-full h-full bg-glass-card backdrop-blur-xl rounded-xl border border-aurora-blue/30 p-4"
          style={{ 
            backfaceVisibility: 'hidden', 
            transform: 'rotateY(180deg)',
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Projected Impact Header */}
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-white">Projected Impact</h4>
            <button
              onClick={() => setIsFlipped(false)}
              className="text-xs text-aurora-blue hover:text-aurora-purple transition"
            >
              Back
            </button>
          </div>

          {/* Impact Metrics */}
          <div className="space-y-3">
            <div className="bg-aurora-blue/10 rounded-lg p-3 border border-aurora-blue/20">
              <div className="text-xs text-gray-400 mb-1">Expected Change</div>
              <div className="text-lg font-bold text-white">
                {card.projected_impact || '+$40/mo Income'}
              </div>
            </div>

            {/* Vision Impacts */}
            {card.vision_impact && Object.keys(card.vision_impact).length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-gray-400">Vision Impact:</div>
                {Object.entries(card.vision_impact).slice(0, 2).map(([visionId, impact]) => (
                  <div key={visionId} className="text-xs text-gray-300">
                    {impact > 0 ? '+' : ''}{(impact * 100).toFixed(1)}% progress
                  </div>
                ))}
              </div>
            )}

            {/* Card Type Info */}
            <div className="mt-4 pt-3 border-t border-gray-700">
              <div className="text-xs text-gray-400">Card Type</div>
              <div className="text-sm text-white capitalize mt-1">
                {card.card_type?.replace('_', ' ') || 'Action'}
              </div>
            </div>
          </div>

          {/* Rarity Gradient Border */}
          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${getCardColor(card.rarity)} opacity-20 -z-10`} />
        </motion.div>
      </div>

      {/* Disabled Overlay */}
      {disabled && (
        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center z-10">
          <span className="text-white text-sm font-semibold">Already Played</span>
        </div>
      )}
    </motion.div>
  );
};

export default StrategyCard;
